import { Box } from '@radix-ui/themes';
import {
  githubDarkTheme,
  githubLightTheme,
  IconChevron,
  JsonEditor,
  ThemeInput,
} from 'json-edit-react';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import useMiscStore from 'stores/use-misc-store';

// Color mapping from RADIX color enums to CSS variables for consistent theming
// TODO: Move to a common utility if reused elsewhere
const RADIX_COLOR_MAP = {
  [RADIX.COLOR.ACCENT]: `var(--${RADIX.COLOR.ACCENT}-11)`, // Accent color
  [RADIX.COLOR.WARNING]: `var(--${RADIX.COLOR.WARNING}-a11)`, // Warning color (alpha)
  [RADIX.COLOR.NEUTRAL]: `var(--${RADIX.COLOR.NEUTRAL}-11)`, // Neutral color
  [RADIX.COLOR.SUCCESS]: `var(--${RADIX.COLOR.SUCCESS}-11)`, // Success color
  [RADIX.COLOR.DANGER]: `var(--${RADIX.COLOR.DANGER}-11)`, // Danger color
  [RADIX.COLOR.INFO]: `var(--${RADIX.COLOR.INFO}-11)`, // Info color
  [RADIX.COLOR.LEFT]: `var(--${RADIX.COLOR.LEFT}-11)`, // Left chevron color
};

// Type-based color mapping for JSON value highlighting
const TYPE_COLOR_MAP = {
  string: RADIX_COLOR_MAP[RADIX.COLOR.ACCENT], // Strings: accent
  number: RADIX_COLOR_MAP[RADIX.COLOR.WARNING], // Numbers: warning

  null: RADIX_COLOR_MAP[RADIX.COLOR.NEUTRAL], // Null: neutral
  undefined: RADIX_COLOR_MAP[RADIX.COLOR.NEUTRAL], // Undefined: neutral

  true: RADIX_COLOR_MAP[RADIX.COLOR.SUCCESS], // Boolean true: success
  false: RADIX_COLOR_MAP[RADIX.COLOR.DANGER], // Boolean false: danger
};

// Theme configuration for the JSON editor, merging custom and library themes
const THEME: ThemeInput = {
  styles: {
    container: {
      backgroundColor: 'transparent', // Transparent background for integration
    },
    bracket: {
      color: RADIX_COLOR_MAP[RADIX.COLOR.SUCCESS], // Bracket color
      cursor: 'pointer',
    },
    itemCount: {
      cursor: 'pointer',
    },
    ...TYPE_COLOR_MAP, // Spread type-based colors
  },
};

/**
 * JsonViewer component
 *
 * Renders a read-only, themed JSON viewer/editor for a given value.
 * Accepts either a JS object or a JSON string. If a string is provided,
 * attempts to parse it as JSON; if parsing fails, displays the string as-is.
 *
 * Props:
 *   - value: object or JSON string to display
 *   - collapse: number of levels to collapse by default (default: 1)
 *   - rootName: optional label for the root node
 */
export const JsonViewer = ({
  value,
  collapse = 1,
  rootName,
}: {
  value?: Record<string, any> | string;
  collapse?: number;
  rootName?: string;
}) => {
  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  // Choose theme based on current appearance (dark/light)
  const theme = isDarkMode
    ? [githubDarkTheme, THEME]
    : [githubLightTheme, THEME];

  const safeValue = useMemo(() => {
    // If value is a string, try to parse as JSON
    try {
      // If no value is provided, render nothing
      if (!value) {
        return undefined;
      }

      if (typeof value === 'string') {
        return JSON.parse(value);
      }

      return value;
    } catch (e) {
      console.error('JsonViewer failed to create safe value', e);
      return value;
    }
  }, [value]);

  if (isNil(safeValue)) {
    return null;
  }

  return (
    // Use a monospace font for JSON
    <Box className="font-mono">
      <JsonEditor
        data={safeValue}
        theme={theme}
        showCollectionCount="when-closed"
        icons={{
          // Custom chevron icon with color
          chevron: (
            <IconChevron
              style={{ color: RADIX_COLOR_MAP[RADIX.COLOR.LEFT] }}
              size="16"
              className="cursor-pointer"
            />
          ),
        }}
        collapse={collapse}
        rootName={rootName}
        viewOnly
      />
    </Box>
  );
};
